import { EllipsisVertical, Flag, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-hot-toast'

import type { FeatureToggleName, UpdateFeatureTogglesBody } from '@ankor-io/common/types/system'
import { FeatureToggleValue } from '@ankor-io/common/types/system'
import {
  Badge,
  Button,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  EmptyState,
  Loading,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ankor-io/ui-components'

import { CreateFeatureFlagDialog } from '@/components/feature-flags/create-feature-flag-dialog'
import { MIN_LOADING_DISPLAY_MS } from '@/constants/ui'
import { useMinimumDisplayTime } from '@/hooks/useMinimumDisplayTime'
import { useGlobalTogglesQuery, useUpdateGlobalTogglesMutation } from '@/queries/system'
import { formatToggleName } from '@/utils/format'

export interface FeatureFlagsTableProps {
  isDialogOpen?: boolean
  setIsDialogOpen?: (open: boolean) => void
}

export function FeatureFlagsTable({
  isDialogOpen: externalDialogOpen,
  setIsDialogOpen: externalSetDialogOpen,
}: FeatureFlagsTableProps = {}) {
  const { data: globalToggles = {}, isPending, error } = useGlobalTogglesQuery()
  const { mutate: updateGlobalToggles, isPending: isUpdating } = useUpdateGlobalTogglesMutation()
  const [internalDialogOpen, setInternalDialogOpen] = useState(false)

  // Use external dialog state if provided, otherwise use internal state
  const isDialogOpen = externalDialogOpen !== undefined ? externalDialogOpen : internalDialogOpen
  const setIsDialogOpen = externalSetDialogOpen || setInternalDialogOpen

  const showLoader = useMinimumDisplayTime(isPending, MIN_LOADING_DISPLAY_MS)

  // Error toast is handled globally by QueryProvider
  // Component still handles mutation errors with specific messages

  const handleToggleChange = (name: string, value: string) => {
    const body: UpdateFeatureTogglesBody = {
      toggles: [
        {
          name: name as FeatureToggleName,
          value: value as FeatureToggleValue,
        },
      ],
    }
    updateGlobalToggles(body, {
      onSuccess: () => {
        toast.success(`${formatToggleName(name as FeatureToggleName)} updated successfully`)
      },
      onError: (error) => {
        toast.error(
          `Failed to update ${formatToggleName(name as FeatureToggleName)}: ${error.message || 'An error occurred'}`,
        )
      },
    })
  }

  const handleCreateGlobalToggle = (toggleName: string, toggleValue: FeatureToggleValue) => {
    const body: UpdateFeatureTogglesBody = {
      toggles: [
        {
          name: toggleName as FeatureToggleName,
          value: toggleValue,
        },
      ],
    }
    updateGlobalToggles(body, {
      onSuccess: () => {
        toast.success(`${formatToggleName(toggleName)} created successfully`)
        setIsDialogOpen(false)
      },
      onError: (error) => {
        toast.error(`Failed to create ${formatToggleName(toggleName)}: ${error.message || 'An error occurred'}`)
      },
    })
  }

  if (error) {
    return <div>Error: {error.message}</div>
  }

  const hasToggles = globalToggles && Object.keys(globalToggles).length > 0

  if (!hasToggles && !isPending) {
    return (
      <>
        <EmptyState
          heading="No global toggles found"
          subheading="Get started by creating a new global toggle."
          icon={<Flag className="mx-auto size-12 text-gray-400" />}
        />
        <div className="flex justify-center mt-6">
          <Button
            color="primary"
            onClick={() => setIsDialogOpen(true)}
          >
            Create Global Toggle
          </Button>
        </div>
        <CreateFeatureFlagDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSubmit={handleCreateGlobalToggle}
          isSubmitting={isUpdating}
          title="Add Feature Flag"
          description="Create a new global feature flag. This will set the default value for all organizations."
          buttonText="Create"
          existingToggleNames={Object.keys(globalToggles)}
        />
      </>
    )
  }

  return (
    <>
      <div className="overflow-x-auto">
        <Table className="max-w-[calc(100vw-2rem)] lg:max-w-[calc(100vw-20rem)]">
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Label</TableHeader>
              <TableHeader>Description</TableHeader>
              <TableHeader>Global Status</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {showLoader ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-12"
                >
                  <div className="flex items-center justify-center">
                    <Loading />
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              Object.entries(globalToggles).map(([name, value]) => (
                <TableRow key={name}>
                  <TableCell>
                    <Badge color="primary">{name}</Badge>
                  </TableCell>
                  <TableCell>{formatToggleName(name)}</TableCell>
                  <TableCell>{}</TableCell>
                  <TableCell>
                    <Switch
                      checked={value === FeatureToggleValue.ENABLED}
                      disabled={isUpdating}
                      onChange={() => {
                        const newValue =
                          value === FeatureToggleValue.ENABLED
                            ? FeatureToggleValue.DISABLED
                            : FeatureToggleValue.ENABLED
                        handleToggleChange(name, newValue)
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <div
                      className="relative z-10 pointer-events-auto"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      <Dropdown>
                        <DropdownButton plain>
                          <EllipsisVertical data-slot="icon" />
                        </DropdownButton>
                        <DropdownMenu anchor="bottom end">
                          <DropdownItem>
                            <Trash2 data-slot="icon" />
                            Delete
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <CreateFeatureFlagDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleCreateGlobalToggle}
        isSubmitting={isUpdating}
        title="Add Feature Flag"
        description="Create a new global feature flag. This will set the default value for all organizations."
        buttonText="Create"
        existingToggleNames={Object.keys(globalToggles)}
      />
    </>
  )
}
